import React, { useState } from "react";
import { Upload, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { AppModal, AppButton } from "../ui";
import apiClient from "../../utils/apiClient";

const MSG = "tw-flex tw-items-center tw-gap-[0.4rem] tw-rounded-[6px] tw-px-2.5 tw-py-2 tw-text-[0.8125rem]";

/**
 * Bulk-import coding questions from a CSV file.
 *
 * CSV columns (header row required):
 *   title, description, difficulty, categories, labels, isPublic, sampleCode, testCases
 *
 * - `categories` and `labels` are pipe-separated (e.g. "JavaScript|Python", "DP|Greedy").
 * - `sampleCode` is JSON keyed by language, e.g. `{"JavaScript":"function solve(){}","Python":"def solve():\n    pass"}`.
 *   These are the HackerRank-style starter templates students see when they open the question.
 * - `testCases` is JSON, e.g. `[{"input":"1 2","expectedOutput":"3","isHidden":false}]`.
 * - `isPublic` is "true" / "false" (only respected when mode === "library").
 *
 * Sample CSV is downloadable via the link inside the modal.
 *
 * Props:
 *   - show, onHide
 *   - mode: "institute" | "library" (passed through to BE; only library can publish)
 *   - onImported: () => void  — called after a successful import
 */

// Minimal RFC-4180-style CSV parser that handles quoted fields and embedded
// commas, double quotes (escaped as ""), and CRLF line endings.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(field); field = ""; }
      else if (ch === '\r' && next === '\n') { row.push(field); rows.push(row); row = []; field = ""; i++; }
      else if (ch === '\n' || ch === '\r') { row.push(field); rows.push(row); row = []; field = ""; }
      else { field += ch; }
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c && c.trim().length > 0));
}

function rowsToQuestions(rows) {
  if (rows.length < 2) return { questions: [], parseErrors: ["CSV must contain a header row and at least one data row."] };
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => header.indexOf(name);
  const iTitle = idx("title");
  const iDesc = idx("description");
  const iDiff = idx("difficulty");
  const iCats = idx("categories");
  const iLabels = idx("labels");
  const iPublic = idx("ispublic");
  const iSample = idx("samplecode");
  const iTests = idx("testcases");
  if (iTitle < 0 || iDesc < 0 || iDiff < 0) {
    return { questions: [], parseErrors: ["CSV header must include at least: title, description, difficulty."] };
  }
  const parseErrors = [];
  const questions = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const get = (i) => (i >= 0 && row[i] != null ? row[i].trim() : "");
    const title = get(iTitle);
    const description = get(iDesc);
    const difficulty = get(iDiff);
    if (!title || !description || !difficulty) {
      parseErrors.push(`Row ${r + 1}: missing title/description/difficulty`);
      continue;
    }
    const cats = get(iCats)
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((entry) => {
        // Accept either plain "<name>" or "<id>:<name>" so the imported
        // question carries the Judge0 language id needed at runtime.
        const colonIdx = entry.indexOf(":");
        if (colonIdx > 0 && /^\d+$/.test(entry.slice(0, colonIdx))) {
          return { id: Number(entry.slice(0, colonIdx)), name: entry.slice(colonIdx + 1).trim() };
        }
        return { name: entry };
      });
    const labels = get(iLabels)
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);
    let testCases = [];
    const rawTests = get(iTests);
    if (rawTests) {
      try {
        const parsed = JSON.parse(rawTests);
        if (Array.isArray(parsed)) testCases = parsed;
      } catch {
        parseErrors.push(`Row ${r + 1}: testCases is not valid JSON, ignored`);
      }
    }
    let sampleCode = {};
    const rawSample = get(iSample);
    if (rawSample) {
      try {
        const parsed = JSON.parse(rawSample);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) sampleCode = parsed;
        else parseErrors.push(`Row ${r + 1}: sampleCode must be a JSON object keyed by language, ignored`);
      } catch {
        parseErrors.push(`Row ${r + 1}: sampleCode is not valid JSON, ignored`);
      }
    }
    const isPublic = (get(iPublic) || "").toLowerCase() === "true";
    questions.push({ title, description, difficulty, categories: cats, labels, sampleCode, testCases, isPublic });
  }
  return { questions, parseErrors };
}

const ImportQuestionsModal = ({ show, onHide, mode = "institute", onImported }) => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setPreviewQuestions([]);
    setParseErrors([]);
    setResult(null);
    setError("");
    setImporting(false);
    setParsing(false);
  };

  const handleClose = () => {
    reset();
    onHide?.();
  };

  const handleFileChange = async (e) => {
    setError("");
    setResult(null);
    setPreviewQuestions([]);
    setParseErrors([]);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    setFile(f);
    setParsing(true);
    try {
      const text = await f.text();
      const rows = parseCsv(text);
      const { questions, parseErrors: errs } = rowsToQuestions(rows);
      setPreviewQuestions(questions);
      setParseErrors(errs);
    } catch (err) {
      setError("Failed to read the CSV file.");
    }
    setParsing(false);
  };

  const handleImport = async () => {
    if (previewQuestions.length === 0) return;
    setError("");
    setImporting(true);
    try {
      const { data } = await apiClient.post("/questions/import", { questions: previewQuestions });
      setResult(data);
      // Auto-refresh parent list, but keep modal open so the user can read the result.
      onImported?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to import questions.");
    }
    setImporting(false);
  };

  return (
    <AppModal
      show={show}
      onHide={handleClose}
      title="Import Coding Questions"
      icon={<Upload size={18} />}
      size="lg"
      footer={(
        <div className="tw-flex tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={handleClose}>Close</AppButton>
          <AppButton
            onClick={handleImport}
            disabled={importing || parsing || previewQuestions.length === 0}
          >
            {importing ? "Importing…" : `Import ${previewQuestions.length} question${previewQuestions.length === 1 ? "" : "s"}`}
          </AppButton>
        </div>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-[0.85rem]">
        <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-sm tw-bg-surface-sunken tw-px-3 tw-py-2.5 tw-text-[0.8125rem] tw-text-content-muted [&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-alt [&_code]:tw-px-1 [&_code]:tw-text-[0.75rem] [&_code]:tw-text-content">
          <FileText size={14} />
          <span>
            CSV columns: <code>title</code>, <code>description</code>, <code>difficulty</code>,
            {" "}<code>categories</code>, <code>labels</code>
            {mode === "library" && <>, <code>isPublic</code></>}
            , <code>sampleCode</code>, <code>testCases</code>.
            {" "}<a className="tw-text-primary tw-underline" href="/coding-questions-sample.csv" download>Download sample CSV</a>
          </span>
        </div>

        <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-sm tw-border tw-border-dashed tw-border-line tw-bg-surface-alt tw-px-3.5 tw-py-3 tw-text-sm tw-text-content-muted hover:tw-border-primary hover:tw-text-content">
          <input className="tw-hidden" type="file" accept=".csv,text/csv" onChange={handleFileChange} />
          <span>{file ? file.name : "Choose a CSV file"}</span>
        </label>

        {parsing && <div className={`${MSG} tw-bg-surface-sunken tw-text-content-muted`}>Parsing CSV…</div>}
        {error && (
          <div className={`${MSG} tw-bg-danger-soft tw-text-danger-fg`}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {parseErrors.length > 0 && (
          <div className="tw-rounded-sm tw-border tw-border-danger/25 tw-bg-danger/[0.06] tw-px-3 tw-py-2.5 tw-text-[0.8125rem] tw-text-danger-fg">
            <div className="tw-mb-1 tw-font-semibold">{parseErrors.length} row issue{parseErrors.length === 1 ? "" : "s"}:</div>
            <ul className="tw-m-0 tw-ps-[1.1rem]">
              {parseErrors.slice(0, 8).map((m) => <li key={m}>{m}</li>)}
              {parseErrors.length > 8 && <li>…and {parseErrors.length - 8} more.</li>}
            </ul>
          </div>
        )}

        {previewQuestions.length > 0 && !result && (
          <div className="tw-rounded-sm tw-border tw-border-line tw-bg-surface-raised tw-px-3 tw-py-2.5 tw-text-[0.8125rem] tw-text-content">
            <div className="tw-mb-[0.4rem] tw-font-semibold">
              Preview — {previewQuestions.length} question{previewQuestions.length === 1 ? "" : "s"} ready
            </div>
            <ul className="tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-ps-[1.1rem]">
              {previewQuestions.slice(0, 5).map((q, i) => (
                <li key={i}>
                  <span className="tw-me-[0.4rem] tw-inline-block tw-rounded-[4px] tw-bg-surface-sunken tw-px-[0.4rem] tw-py-[0.05rem] tw-text-[0.7rem] tw-text-content-muted">{q.difficulty}</span> {q.title}
                </li>
              ))}
              {previewQuestions.length > 5 && <li>…and {previewQuestions.length - 5} more.</li>}
            </ul>
          </div>
        )}

        {result && (
          <div className={`${MSG} tw-bg-success-soft tw-text-success-fg`}>
            <CheckCircle2 size={14} />
            <span>
              Imported {result.insertedCount} question{result.insertedCount === 1 ? "" : "s"}.
              {result.errorCount > 0 && ` ${result.errorCount} skipped.`}
            </span>
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default ImportQuestionsModal;
